/**
 * Forgot Password Page
 * Allows users to request a password reset email
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../services/supabase";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Card from "../components/ui/Card";

const RESET_REQUEST_TIMEOUT_MS = 15_000;

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    try {
      setIsLoading(true);

      // Route through send-auth-email so the email is branded AND interstitial-first:
      // it links to /confirm-reset (our app) rather than the direct Supabase verify URL,
      // so email security scanners can't consume the one-time recovery token on prefetch.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const { error: invErr } = await Promise.race([
          supabase.functions.invoke("send-auth-email", {
            body: { type: "password_reset", email },
          }),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error("Password reset request timed out")),
              RESET_REQUEST_TIMEOUT_MS,
            );
          }),
        ]);

        if (invErr) {
          console.warn("Password reset request did not complete cleanly.");
        }
      } catch {
        // Neutral UX: never reveal whether an account exists (matches the prior
        // resetPasswordForEmail behavior). Show the same "check your email" state
        // even after a transport timeout: the backend may already have accepted
        // the request and sent the email. No email/token in logs.
        console.warn("Password reset request did not complete cleanly.");
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }

      setSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/logowhite.png"
            alt="U.S. LawShield"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your email address and we'll send you a link to reset your
          password
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card padding="lg">
          {success ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Check your email
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>
                        We've sent a password reset link to{" "}
                        <strong>{email}</strong>. Click the link in the email to
                        reset your password.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Didn't receive an email? Check your spam folder or{" "}
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail("");
                  }}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  try again
                </button>
              </p>

              <div className="pt-4">
                <Link
                  to="/login"
                  className="block w-full text-center px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  ← Back to login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <Input
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                fullWidth
                autoFocus
              />

              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading}
              >
                Send Reset Link
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  ← Back to login
                </Link>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
